/**
 * Gift Store
 *
 * Manages gift state including sent/received gifts, pending gift notifications,
 * and gift permissions. Users can gift challenges to others by user ID or email.
 * Gift permissions control who can send gifts to the user (everyone, friends only,
 * specific allow/block lists). Not available to Tier 1 (school-only) accounts.
 *
 * @module useGiftStore
 *
 * @example
 * ```tsx
 * import { useGiftStore, selectPendingGiftCount } from '@/store/useGiftStore'
 *
 * function GiftBadge() {
 *   const { fetchPendingGifts } = useGiftStore()
 *   const pendingCount = useGiftStore(selectPendingGiftCount)
 *
 *   useEffect(() => { fetchPendingGifts() }, [])
 *
 *   return pendingCount > 0 ? <Badge count={pendingCount} /> : null
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  Gift,
  GiftPermission,
  GiftRecipientSearchResult,
  GiftPermissionType,
} from '../types'
import { giftApi } from '../api'

interface GiftState {
  // Gifts
  sentGifts: Gift[]
  receivedGifts: Gift[]
  pendingGifts: Gift[]

  // Permissions
  permissions: GiftPermission | null

  // Recipient search
  searchResults: GiftRecipientSearchResult[]
  isSearching: boolean

  // Loading states
  isLoading: boolean
  isSaving: boolean

  // Error state
  error: string | null

  // Actions - Gifts
  fetchSentGifts: () => Promise<void>
  fetchReceivedGifts: () => Promise<void>
  fetchPendingGifts: () => Promise<void>
  sendGift: (data: {
    challengeId: string
    recipientUserId?: string
    recipientEmail?: string
    personalMessage?: string
    communityId?: string
  }) => Promise<Gift | null>
  acceptGift: (giftId: string) => Promise<boolean>
  declineGift: (giftId: string) => Promise<boolean>
  cancelGift: (giftId: string) => Promise<boolean>

  // Actions - Permissions
  fetchPermissions: () => Promise<void>
  updatePermissions: (data: {
    permissionType: GiftPermissionType
    allowedUserIds?: string[]
    blockedUserIds?: string[]
  }) => Promise<boolean>

  // Actions - Search
  searchRecipients: (query: string, communityId?: string) => Promise<void>
  clearSearchResults: () => void

  clearError: () => void
  reset: () => void
}

const initialState = {
  sentGifts: [],
  receivedGifts: [],
  pendingGifts: [],
  permissions: null,
  searchResults: [],
  isSearching: false,
  isLoading: false,
  isSaving: false,
  error: null,
}

export const useGiftStore = createWithEqualityFn<GiftState>()((set, get) => ({
  ...initialState,

  // ============================================================================
  // Gifts
  // ============================================================================

  fetchSentGifts: async () => {
    set({ isLoading: true, error: null })

    const response = await giftApi.getSentGifts()

    if (response.success && response.data) {
      set({ sentGifts: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load sent gifts',
        isLoading: false,
      })
    }
  },

  fetchReceivedGifts: async () => {
    set({ isLoading: true, error: null })

    const response = await giftApi.getReceivedGifts()

    if (response.success && response.data) {
      set({ receivedGifts: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load received gifts',
        isLoading: false,
      })
    }
  },

  fetchPendingGifts: async () => {
    const response = await giftApi.getPendingGifts()

    if (response.success && response.data) {
      set({ pendingGifts: response.data })
    }
  },

  sendGift: async (data) => {
    set({ isSaving: true, error: null })

    const response = await giftApi.sendGift(data)

    if (response.success && response.data) {
      set({
        sentGifts: [response.data, ...get().sentGifts],
        isSaving: false,
      })
      return response.data
    } else {
      set({
        error: response.error ?? 'Failed to send gift',
        isSaving: false,
      })
      return null
    }
  },

  acceptGift: async (giftId) => {
    set({ isSaving: true, error: null })

    const response = await giftApi.acceptGift(giftId)

    if (response.success && response.data) {
      // Update in all lists
      const updateGift = (gifts: Gift[]) =>
        gifts.map((g) => (g.id === giftId ? response.data! : g))

      set({
        receivedGifts: updateGift(get().receivedGifts),
        pendingGifts: get().pendingGifts.filter((g) => g.id !== giftId),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to accept gift',
        isSaving: false,
      })
      return false
    }
  },

  declineGift: async (giftId) => {
    set({ isSaving: true, error: null })

    const response = await giftApi.declineGift(giftId)

    if (response.success && response.data) {
      const updateGift = (gifts: Gift[]) =>
        gifts.map((g) => (g.id === giftId ? response.data! : g))

      set({
        receivedGifts: updateGift(get().receivedGifts),
        pendingGifts: get().pendingGifts.filter((g) => g.id !== giftId),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to decline gift',
        isSaving: false,
      })
      return false
    }
  },

  cancelGift: async (giftId) => {
    set({ isSaving: true, error: null })

    const response = await giftApi.cancelGift(giftId)

    if (response.success) {
      set({
        sentGifts: get().sentGifts.map((g) =>
          g.id === giftId ? { ...g, status: 'cancelled' as const } : g
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to cancel gift',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Permissions
  // ============================================================================

  fetchPermissions: async () => {
    const response = await giftApi.getGiftPermissions()

    if (response.success) {
      set({ permissions: response.data })
    }
  },

  updatePermissions: async (data) => {
    set({ isSaving: true, error: null })

    const response = await giftApi.updateGiftPermissions(data)

    if (response.success && response.data) {
      set({ permissions: response.data, isSaving: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update permissions',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Search
  // ============================================================================

  searchRecipients: async (query, communityId?) => {
    if (query.length < 2) {
      set({ searchResults: [] })
      return
    }

    set({ isSearching: true })

    const response = await giftApi.searchRecipients(query, communityId)

    if (response.success && response.data) {
      set({ searchResults: response.data, isSearching: false })
    } else {
      set({ isSearching: false })
    }
  },

  clearSearchResults: () => set({ searchResults: [] }),

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}))

// ============================================================================
// Selectors
// ============================================================================

/** Select all gifts sent by the current user. */
export const selectSentGifts = (state: GiftState) => state.sentGifts

/** Select all gifts received by the current user. */
export const selectReceivedGifts = (state: GiftState) => state.receivedGifts

/** Select gifts awaiting the user's accept/decline decision. */
export const selectPendingGifts = (state: GiftState) => state.pendingGifts

/** Select the count of pending gifts (for notification badges). */
export const selectPendingGiftCount = (state: GiftState) => state.pendingGifts.length
